/**
 * O que o aviso DIZ — card 1864753210497959381.
 *
 * A pergunta que o card manda responder não é *"por que travou"*, é **"como a pessoa fica
 * sabendo"**. Então o texto é parte da entrega, não enfeite dela, e mora num arquivo só —
 * se estivesse espalhado pelo componente, a próxima mudança de regra deixaria metade das
 * frases descrevendo o comportamento antigo.
 *
 * Três princípios, e os três vieram do incidente:
 *
 * 1. **Nunca dizer "erro de sincronização".** Quem perdeu 45 minutos não precisa do nome do
 *    subsistema; precisa saber que o trabalho está só ali e que fechar a aba o apaga.
 * 2. **Nunca dizer bytes.** O card proíbe, e a Regra 8.64 explica por quê.
 * 3. **"Não sei" é uma frase, não um silêncio** (Regra 8.14).
 */

import type { EstadoDoRelogio } from './relogio-da-fila';
import type { ResultadoDaProva } from './prova-do-servidor';

export type Tom = 'alerta' | 'atencao' | 'neutro';

export interface Redacao {
  tom: Tom;
  titulo: string;
  detalhe: string;
  /** a linha da prova; vazia quando ainda não houve prova nenhuma */
  prova: string;
  /** o rótulo do botão que refaz a prova agora */
  acao: string;
}

export function duracaoEmPortugues(ms: number): string {
  const segundos = Math.floor(ms / 1000);
  if (segundos < 60) {
    return `${segundos} segundo${segundos === 1 ? '' : 's'}`;
  }
  const minutos = Math.floor(segundos / 60);
  if (minutos < 60) {
    return `${minutos} minuto${minutos === 1 ? '' : 's'}`;
  }
  const horas = Math.floor(minutos / 60);
  const resto = minutos % 60;
  const cabeca = `${horas} hora${horas === 1 ? '' : 's'}`;
  return resto === 0 ? cabeca : `${cabeca} e ${resto} min`;
}

export function frasePendencia(pendenteHa: number | null): string {
  return pendenteHa === null
    ? 'há algum tempo'
    : `há ${duracaoEmPortugues(pendenteHa)}`;
}

export function fraseDaProva(prova: ResultadoDaProva | null): string {
  if (!prova) {
    return '';
  }
  switch (prova.veredito) {
    case 'em-dia':
      return 'Conferido direto no servidor: ele tem tudo o que está nesta tela.';
    case 'servidor-atrasado': {
      const n = prova.operacoesFaltando;
      return `Conferido direto no servidor: faltam ${n} alteraç${n === 1 ? 'ão' : 'ões'} sua${n === 1 ? '' : 's'} lá.`;
    }
    case 'nao-verificavel':
      // Nem verde nem vermelho — e dito com todas as letras, porque "não consegui conferir"
      // e "está tudo certo" mandam a pessoa a lugares diferentes.
      return `Não consegui conferir no servidor: ${prova.porque}.`;
  }
}

export function redigir(
  estado: EstadoDoRelogio,
  prova: ResultadoDaProva | null
): Redacao {
  const quanto = frasePendencia(estado.pendenteHa);
  const linhaDaProva = fraseDaProva(prova);
  const acao = 'Conferir agora';

  // O caso que só a prova enxerga: a fila está parada, mas o documento ABERTO já subiu.
  // Dizer "seu trabalho não foi salvo" aqui seria mentira — o que está parado é outro
  // documento do workspace, e a pessoa merece essa distinção.
  if (prova?.veredito === 'em-dia' && estado.gravidade !== 'erro') {
    return {
      tom: 'atencao',
      titulo: 'Este documento está salvo no servidor.',
      detalhe: `Mas há trabalho de OUTROS documentos parado ${quanto} na fila deste navegador. Vale deixar esta aba aberta até a fila descer.`,
      prova: linhaDaProva,
      acao,
    };
  }

  if (!estado.online) {
    return {
      tom: 'alerta',
      titulo: 'Sem conexão — o que você escreveu está só neste navegador.',
      detalhe: `Nada sobe ao servidor ${quanto}. Não feche esta aba até a conexão voltar: o trabalho ainda não saiu daqui.`,
      prova: linhaDaProva,
      acao,
    };
  }

  if (estado.gravidade === 'erro') {
    return {
      tom: 'alerta',
      titulo: 'Não estou conseguindo salvar no servidor.',
      detalhe: estado.erro
        ? `O servidor recusou ou caiu ${quanto}. O que você escreveu continua só neste navegador. Motivo relatado: ${estado.erro}`
        : `Falhou ${quanto} e continua tentando. O que você escreveu continua só neste navegador.`,
      prova: linhaDaProva,
      acao,
    };
  }

  // `travado`: o caso do incidente. Conexão de pé, sem erro nenhum, e a fila não desce.
  return {
    tom: 'alerta',
    titulo: 'O que você escreveu ainda não saiu deste navegador.',
    detalhe: `A fila está parada ${quanto} — bem acima do normal, e sem nenhum erro. Se fechar esta aba agora, este trabalho se perde.`,
    prova: linhaDaProva,
    acao,
  };
}

/** O selo compacto, para quando a pessoa encolhe o aviso. Ele NUNCA some (Regra 8.79). */
export function redigirSelo(estado: EstadoDoRelogio): string {
  return estado.gravidade === 'erro'
    ? 'Não está salvando no servidor'
    : `Sem salvar ${frasePendencia(estado.pendenteHa)}`;
}

/**
 * ⚠️ O texto do `beforeunload` é IGNORADO por todos os navegadores desde 2017 — eles
 * desenham um diálogo genérico. Ele existe aqui para navegador antigo e para deixar
 * explícito, no código, que a frase que importa é a da PÁGINA, não a do diálogo.
 */
export const AVISO_AO_FECHAR =
  'Há trabalho que ainda não subiu ao servidor. Se fechar agora, ele se perde.';
